// Displays an item, like a vessel, engine, generator, etc -- ID comes from a parameter in the URL.

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { itemFields } from '../../fleet-shared/ItemFields.mjs';
import { 
  useStatusMessage, 
  Attachments,  
  EquipmentInfoUI, 
  EquipmentList, 
  ModalEditForm, 
  HourMeter, 
  UpcomingTasks, 
  Confirm } from '../../components';
import { ItemLogbooks } from '../logbook/ItemLogbooks';
import TripsSummary from '../trip/TripsSummary';
import { usePlaceIndex } from '../../utilities/usePlaceIndex';
import * as TaskBuckets from '../../fleet-shared/TaskBuckets.mjs';
import { isLogOnly } from '../../fleet-shared/ItemTracking.mjs';
import { deleteCounter, updateCounter, NewItemName, createItem, deleteItem, createTask, saveEditedItem, listLogbooksWithRecentEntries, retrieveItem } from '../../utilities/Database.mjs';
import { urlForItem, urlForAllItems, urlForAllTrips, urlForItemStorage, urlForTasks, urlForTaskId, urlForEmailItem } from '../../fleet-shared/Urls.mjs';
import { useEditTaskPopup } from '../task/useEditTaskPopup';
import { validateItem } from '../../utilities/Validators.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { loadUpcomingTripsFresh } from '../../utilities/TripSchedule.mjs';
import { ItemBreadcrumbs } from './ItemBreadcrumbs';

const ItemUI = ( {item, identityId, showText, showError, hideStatus, setItem} ) => {

  // Various status badges:
  const [counterItems, setCounterItems] = useState(null);

  // The task buckets for this item's tasks (see utitlies/TaskBuckets.js)
  const [upcomingTaskBuckets, setUpcomingTaskBuckets] = useState(null);

  // Edit Item UI.  When to show it, and what to show.
  const [showEditItem, setShowEditItem] = useState(false);
  const [editItem, setEditItem] = useState(null);

  // Confirm delete
  const [showConfirm, setShowConfirm] = useState(false);

  const navigate = useNavigate();

  // The gazetteer, for the Upcoming Trips card's place names.
  const placeIndex = usePlaceIndex();

  // Deep-link from the status email: `/me/item/:id?counter=<id>` should pop
  // the Edit Hours modal for that counter so the recipient can update the
  // reading without having to hunt for the right row.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCounterId = searchParams.get('counter');
  const consumeInitialCounter = useCallback(() => {
    if (!searchParams.get('counter')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('counter');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const taskEdited = useCallback(() => {
    // Update the coming soon tasks UI
    const buckets = TaskBuckets.taskBuckets(item);
    setUpcomingTaskBuckets(buckets);    
  }, [item]);

  const taskDeleted = useCallback(() => {
    // Update the coming soon tasks UI
    const buckets = TaskBuckets.taskBuckets(item);
    setUpcomingTaskBuckets(buckets);
  }, [item]);

  // Popup for editing a task. Returns function to show the popup and the popup itself to be put in the UI.
  const { showEditTaskPopup, TaskEditor } = useEditTaskPopup({ item, taskEdited, taskDeleted });


  // Whenever the item changes, I update the Counter/Upcoming Tasks
  useEffect( () => {
    console.info('updateUI');

    // Update the counter UI.
    if ((null != item) && (null != item.itemCounter)) {
      setCounterItems(item.itemCounter.items);
    } else {
      setCounterItems(null);
    }

    // Update the coming soon tasks UI
    const buckets = TaskBuckets.taskBuckets(item);
    setUpcomingTaskBuckets(buckets);

    return () => {
      console.log("updateUI unmounted");
    }
  }, [item]);


  // State to show the all tasks pane.
  const [showTaskBucket, setShowTaskBucket] = useState(false);
  const [taskBucket, setTaskBucket] = useState(null);

  // When the item changes, update the UI. 
  useEffect( () => {
    // If showTaskBucket changes to true, goto the tasks page.
    if (showTaskBucket) {
      navigate(urlForTasks(item.id, taskBucket.tasksUrl));
    }
  }, [item, navigate, showTaskBucket, taskBucket]);


  // Bring up the edit UI for this item.
  const editTapped = useCallback(() => {
    setEditItem(item);
    setShowEditItem(true);
  }, [item]);



  // Delete the given counterId
  const deleteCounterItem = useCallback(async(id) => {
    // This can throw, caller catches and shows error...
    await deleteCounter(id, item.itemCounter.items);
  }, [item]);



  // Passed to EditHours; runs once per autosave flush. throws on errors.
  const saveCounter = useCallback(async (counter) => {
    // This can throw, caller catches and shows error...
    // updateCounter splices the saved counter into the in-memory list; the
    // copy-and-set forces a re-render so the HourMeter repaints with it
    // (with autosave there's no later modal-close render to piggyback on).
    await updateCounter(counter, item.itemCounter.items);
    setItem({ ...item });
  }, [item, setItem]);


  // Confirm delete, then delete item.
  const deleteTapped = useCallback(() => {
    setShowConfirm(true);
  }, [setShowConfirm]);


  // User confirmed they want to delete this item.
  const onConfirmDelete = useCallback( async () => {
    showText("Deleting...")

    try {
      setShowConfirm(false);

      // Cancel any pending or in-flight autosave for this item first -- the
      // edit modal flushes on close, so a save can still be in flight here,
      // and it would land against a deleted row and park the global status
      // pill in "Couldn't save" after the user has already navigated away.
      saveQueue.cancel(`item:${item.id}`);

      // Delete the item and all its children:
      await deleteItem(item);

      // Status is good:
      hideStatus();

      // Go to parent, as this guy is gone...
      if (item.parentId != null) {
        navigate(urlForItem(item.parentId), { replace: true });
      } else {
        navigate(urlForAllItems(), {replace: true });;
      }
    } catch (err) {
      setShowConfirm(false);
      showError(err);
    }
  }, [showText, setShowConfirm, hideStatus, showError, item, navigate]);



  // Passed to EditItem.  When the Save button is pressed, this is called.
  // throws on errors.
  //
  // The form's `newItem` payload has only the fields declared in `itemFields`
  // (no `id`), so we must use `editItem` -- the row the modal was opened on
  // -- as the source of truth for which record to update. Passing the parent
  // `item` here would silently rewrite the parent every time the user edited
  // a piece of equipment.
  const saveEditedItemForm = useCallback(async (newItem) => {
    // This can throw, caller catches and shows error...
    let savedItem = await saveEditedItem(newItem, editItem);

    // Editing this item?  Update the UI.
    //
    // Use `savedItem` (the AppSync response) rather than `newItem` (the form
    // payload) so server-computed fields like `updatedAt` are reflected in
    // the UI immediately, matching the equipment branch below. The mutation
    // response contains only scalar fields, so nested arrays on `item`
    // (childItems, tasks, completedTasks, itemCounter, emails, parentItem)
    // are intentionally left untouched.
    if (item.id === editItem.id) {
      Object.assign(item, savedItem);
    } else {
      // TODO: Can't equipment have equipment. i.e. should this be recursive?
      // or no -- cuz can only edit a child in current UI, not a child of a child.

      // No, editing some equipment, update UI for that...
      const cItems = item.childItems.items.length;
      for (var i=0; i < cItems; i++) {
        if (item.childItems.items[i].id === editItem.id) {
          item.childItems.items[i] = savedItem;
          break;
        }
      }
    }

    // Copy the item and set it again to force a re-render of the outer <Item>
    // component, which owns the breadcrumbs. Without this the in-place
    // Object.assign above updates the body (re-rendered here) but not the
    // breadcrumb name, since the outer component never sees a new reference.
    // Runs per autosave flush -- must NOT close the modal.
    setItem({ ...item });
  }, [item, editItem, setItem]);

  
  // Adds new equpment to the item.  Throws on error.  Returns a link to the new equipment.
  const addEquipment = useCallback(async () => {
    // Add item to the database
    let newItem = {
      name: NewItemName,
      parentId: item.id
    };

    // This can throw, caller catches and shows error...
    const createdItem = await createItem(newItem);
    item.childItems.items = [...item.childItems.items, createdItem];

    setEditItem(createdItem);
    setShowEditItem(true);

    return createdItem;
  }, [setEditItem, setShowEditItem, item]);


  // Adds a new task to this item. Throws on error.
  const addTask = useCallback(async () => {
      var task = await createTask(item);

      // Update the model. I copy the item and set it again to force the re-render
      let newItem = {...item};
      newItem.tasks.items.push(task);
      setItem(newItem);

      // Show the edit task UI
      showEditTaskPopup(task);
  }, [item, setItem, showEditTaskPopup]);


  // When click on a task, go to the task detail page. Pass the upcoming-tasks bucket so Task page
  // delete navigates back to /tasks/:listType instead of the item summary.
  const onClickTask = useCallback((task, tasksListBucket) => {
    const url = urlForTaskId(task.itemId, task.id);
    if (tasksListBucket) {
      navigate(url, { state: { tasksListBucket } });
    } else {
      navigate(url);
    }
  }, [navigate]);


  const emailTapped = useCallback(() => {
      navigate(urlForEmailItem(item.id));
  }, [navigate, item]);


  // A log-only item (ItemTracking.mjs) hides its maintenance surfaces --
  // counters, tasks, sub-equipment, and the status-digest email button (that
  // mail is a tasks/counters digest). Presentation only, never authorization:
  // the task pages stay reachable by URL, so an item downgraded after
  // collecting tasks keeps its data readable, and re-choosing Full tracking
  // in the edit modal brings every card straight back.
  const logOnly = isLogOnly(item);

  return (
    (item != null) &&
    <div className="pb-3">
      <EquipmentInfoUI
        identityId={identityId}
        item={item}
        deleteTapped={deleteTapped}
        editTapped={editTapped}
        emailTapped={logOnly ? undefined : emailTapped} />

      {!logOnly && <HourMeter
        itemId={item.id}
        counterItems={counterItems}
        initialCounterId={initialCounterId}
        onConsumeInitialCounter={consumeInitialCounter}
        save={saveCounter}
        delete={deleteCounterItem} />}

      {!logOnly && <UpcomingTasks
        buckets={upcomingTaskBuckets}
        onClickTask={onClickTask}
        setTaskBucket={setTaskBucket}
        setShowTaskBucket={setShowTaskBucket}
        addTask={addTask}
      />}

      <ItemLogbooks
        item={item}
        setItem={setItem}
      />

      {/* "See All" leaves the item behind for the cross-item Trips page --
          Trips is a top-level section now, and that page carries this item's
          own trips list one hop further in. */}
      <TripsSummary
        item={item}
        placeIndex={placeIndex}
        onSeeAll={() => navigate(urlForAllTrips())}
      />

      {!logOnly && <EquipmentList
        equipment={ item.childItems.items || [] }
        heading={ (item.parentId == null) ? "Equipment" : "Parts"}
        addEquipment={ addEquipment }
      />}

      <ModalEditForm
        show={showEditItem}
        fields={itemFields}
        onHide={() => setShowEditItem(false)}
        item={editItem}
        save={saveEditedItemForm}
        validate={validateItem}
        recordKeyPrefix="item"
      />
      
      {TaskEditor}

      <Confirm
        show={showConfirm}
        onConfirm={onConfirmDelete}
        onHide = {() => setShowConfirm(false)}
        body='Are you sure?'
        confirmText='Confirm Delete'
        title={`Delete ${item.name}`}>
      </Confirm>
    </div>
  )
}



const Item = ({user}) => {
  // Loading/error UI
  const [StatusMessage, {showLoading, showText, showError, hideStatus}] = useStatusMessage();

  // Passed on URL:
  const { itemId } = useParams();
  const [item, setItem] = useState(null);

  // When the itemId changes, retrieve the item.
  useEffect(() => {
    let mounted = true;
    // Lets the lane wait below be dropped on unmount instead of holding a
    // queue subscription until (or unless) the write finishes.
    const leaving = new AbortController();
    async function loadItem(id) {
      try {
        showLoading();
        // retrieveItem loads the item and fully paginates its nested
        // connections (tasks, childItems, counters). Logbooks (and their most
        // recent entries) are loaded separately: the deep-get relationship
        // can't sort entries by date, and an item may have several logbooks
        // each needing its own date-sorted recent list. Trips likewise need a
        // date-sorted, upcoming-only query.
        // loadUpcomingTripsFresh owns the "don't paint a leg someone is still
        // saving" dance -- see its comment, and README.md § "Autosave" ("Before reading a record
        // another surface may have just edited").
        const [loadedItem, logbooks, trips] = await Promise.all([
          retrieveItem(id),
          listLogbooksWithRecentEntries(id),
          loadUpcomingTripsFresh(id, { signal: leaving.signal }),
        ]);
        if (!mounted) return;

        loadedItem.logbooks = { items: logbooks };
        loadedItem.trips = { items: trips };

        setItem(loadedItem);
        hideStatus();
      } catch (err) {
        if (mounted) showError(err);
      }
    }
    loadItem(itemId);
    return () => { mounted = false; leaving.abort(); };
  }, [hideStatus, itemId, showError, showLoading]);

  return (
    <div>
      <ItemBreadcrumbs item={item} />

      <StatusMessage/>
      <ItemUI identityId={user ? user.identityId : null} item={item} showText={showText} showError={showError} hideStatus={hideStatus} setItem={setItem}/>
      <Attachments bucket={urlForItemStorage(itemId)} hideLoading={(item === null)} />
    </div>
  )
}

export default Item;

